"use client";

import { motion } from "framer-motion";
import Cookies from "js-cookie";
import { Globe } from "lucide-react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "next/navigation";

const LANGS: { code: "en" | "ar"; label: string; full: string }[] = [
  { code: "en", label: "EN", full: "English" },
  { code: "ar", label: "عربي", full: "العربية" },
];

export default function LanguageSwitcher() {
  const active = useLocale();
  const pathname = usePathname();
  const router = useRouter();

  const changeLang = (next: "ar" | "en") => {
    if (next === active) return;
    Cookies.set("DAWLIA_NEXT_LOCALE", next, { path: "/", expires: 30 });

    const current = pathname.split("/")[1];
    const clean = pathname.startsWith(`/${current}`)
      ? pathname.slice(current.length + 1)
      : pathname;
    const qs = window.location.search;
    router.push(clean ? `/${next}/${clean}${qs}` : `/${next}${qs}`);
  };

  return (
    <div
      dir="ltr"
      role="group"
      aria-label="Language"
      className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-gray-200 shadow-sm"
    >
      <span className="hidden sm:flex w-7 h-7 items-center justify-center text-brand-navy/50">
        <Globe size={15} />
      </span>
      {LANGS.map((l) => {
        const isActive = active === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => changeLang(l.code)}
            aria-pressed={isActive}
            title={l.full}
            className={`relative h-8 min-w-[52px] px-3 rounded-full text-[12px] font-black tracking-wide transition-colors ${
              isActive ? "text-white" : "text-brand-navy/60 hover:text-brand-navy"
            }`}
          >
            {isActive && (
              <motion.span
                layoutId="lang-pill"
                className="absolute inset-0 rounded-full bg-brand-navy shadow-md shadow-brand-navy/30"
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            )}
            <span className="relative">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
}
